import { Link } from "react-router";
import { FloatingFocusManager, FloatingPortal } from "@floating-ui/react";
import { FloatingLayerProvider, Icon } from "@sellar-io/components";
import {
  useEffect,
  useId,
  type ComponentPropsWithRef,
  type ReactNode,
  type RefObject,
} from "react";
import { useMediaQuery } from "usehooks-ts";
import { PopoverContent } from "../../../components/popover/PopoverContent";
import { PopoverContext, usePopover } from "../../../hooks/usePopover";
import type { ProductCategoryPreset } from "../../discovery/components/productCategoryPresets.shared";
import { buildProductCategoryPresetLocation } from "../../discovery/components/productCategoryPresets.shared";
import { useNavigationOverlay } from "./NavigationOverlayContext.shared";

export type ProductCategoryOverlayConfig = {
  categoryKey: ProductCategoryPreset["categoryKey"];
  label: string;
  groups: readonly {
    id: string;
    options: readonly {
      id: string;
      label: string;
      preset: Omit<ProductCategoryPreset, "categoryKey">;
    }[];
  }[];
};

export const ProductCategoryOverlay = ({
  config,
  navigationRef,
  trigger,
}: {
  config: ProductCategoryOverlayConfig;
  navigationRef: RefObject<HTMLElement | null>;
  trigger: (props: ComponentPropsWithRef<"button">) => ReactNode;
}) => {
  const id = `product-category:${config.categoryKey}`;
  const panelId = useId();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const { activeOverlay, openOverlay, closeOverlay } = useNavigationOverlay();
  const open = activeOverlay === id;
  const popover = usePopover({
    open,
    onOpenChange: (next) => {
      if (!next) {
        closeOverlay(id);
      }
    },
    trigger: "click",
    placement: "bottom-start",
    disableFlip: isMobile,
    offset: isMobile ? 0 : 8,
    viewportPadding: isMobile ? 0 : 8,
  });
  const { setPositionReference } = popover.refs;
  useEffect(() => {
    setPositionReference(
      isMobile ? navigationRef.current : popover.refs.domReference.current,
    );
  }, [
    isMobile,
    navigationRef,
    setPositionReference,
    popover.refs.domReference,
    open,
  ]);

  return (
    <FloatingLayerProvider host="navigationBar">
      <PopoverContext.Provider value={popover}>
        {trigger({
          ...popover.getReferenceProps({
            onClick: () => {
              if (open) {
                closeOverlay(id);
              } else {
                openOverlay(id);
              }
            },
          }),
          ref: popover.refs.setReference,
          "aria-haspopup": "dialog",
          "aria-expanded": open,
          "aria-controls": open ? panelId : undefined,
        })}
        {open && (
          <FloatingPortal>
            <FloatingFocusManager
              context={popover.context}
              modal={false}
              initialFocus={popover.refs.floating}
              returnFocus
            >
              <PopoverContent
                portal={false}
                id={panelId}
                role="dialog"
                aria-label={`${config.label} products`}
                tabIndex={-1}
                className="w-[216px] rounded-2xl border-zinc-200 p-4 text-sm leading-5 shadow-[2px_2px_16px_0_#0000000f] outline-none max-[640px]:w-screen max-[640px]:rounded-none max-[640px]:border-x-0 max-[640px]:px-4 max-[640px]:pt-0"
              >
                {config.groups.map((group, index) => (
                  <div
                    key={group.id}
                    className={
                      index ? "mt-2 border-t border-zinc-200 pt-2" : undefined
                    }
                  >
                    {group.options.map((option) => (
                      <Link
                        key={option.id}
                        to={buildProductCategoryPresetLocation({
                          ...option.preset,
                          categoryKey: config.categoryKey,
                        })}
                        onClick={() => closeOverlay(id)}
                        className="flex min-h-7 w-full cursor-pointer items-center justify-between gap-3 rounded py-1 text-left text-sm text-zinc-800 hover:text-[#8c23c7] focus-visible:outline-2 focus-visible:outline-purple-600 max-[640px]:min-h-9 max-[640px]:px-0"
                      >
                        <span>{option.label}</span>
                        <Icon
                          icon="CHEVRON_RIGHT"
                          size="16px"
                          className="shrink-0"
                        />
                      </Link>
                    ))}
                  </div>
                ))}
              </PopoverContent>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </PopoverContext.Provider>
    </FloatingLayerProvider>
  );
};
